import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { buildCrmSwalBody, fireConfirmDialog, fireCrmAlert } from '../utils/swalConfirm';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import UiIcon from '../components/UiIcon';
import AmvTablePager from '../components/admin/AmvTablePager';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/admin-medicos-visibilidad-premium.css';

const ENDPOINTS = {
  list: '/api/asociacion-recetas/delivery-config/visibilidad',
  create: '/api/asociacion-recetas/delivery-config',
  patch: (id) => `/api/asociacion-recetas/delivery-config/${id}/activa`,
  update: (id) => `/api/asociacion-recetas/delivery-config/${id}`,
  delete: (id) => `/api/asociacion-recetas/delivery-config/${id}`
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function showToast({ title, text, icon = 'success' }) {
  Swal.fire({
    toast: true,
    position: 'bottom-end',
    icon,
    title,
    text,
    showConfirmButton: false,
    timer: 3200,
    timerProgressBar: true,
    customClass: { popup: 'swal-toast-crm swal-toast-crm--amv' }
  });
}

function sortRows(list) {
  return [...list].sort((a, b) => {
    const aActive = Number(a?.is_active) === 1 ? 1 : 0;
    const bActive = Number(b?.is_active) === 1 ? 1 : 0;
    if (aActive !== bActive) return bActive - aActive;
    const aName = String(a?.nombre || '').trim();
    const bName = String(b?.nombre || '').trim();
    return aName.localeCompare(bName, 'es', { sensitivity: 'base' });
  });
}

function DeliveryConfig() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [savingIds, setSavingIds] = useState({});
  const [deletingIds, setDeletingIds] = useState({});
  const [creating, setCreating] = useState(false);
  const [newNombre, setNewNombre] = useState('');
  const [newDescripcion, setNewDescripcion] = useState('');
  const [newIsActive, setNewIsActive] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');
  const [editIsActive, setEditIsActive] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.list}`, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const list = Array.isArray(json?.deliveries) ? json.deliveries : [];
      setDeliveries(sortRows(list));
    } catch (e) {
      setError(e.message || 'No se pudo cargar delivery.');
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(() => sortRows(deliveries), [deliveries]);

  const filteredRows = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter((row) => {
      const nombre = String(row?.nombre || '').toLowerCase();
      const descripcion = String(row?.descripcion || '').toLowerCase();
      return nombre.includes(q) || descripcion.includes(q);
    });
  }, [search, sortedRows]);

  const totalPages = useMemo(() => {
    if (pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(filteredRows.length / pageSize));
  }, [filteredRows.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    if (pageSize <= 0) return filteredRows;
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const activeCount = useMemo(
    () => deliveries.filter((row) => Number(row.is_active) === 1).length,
    [deliveries]
  );

  const inactiveCount = Math.max(0, deliveries.length - activeCount);
  const searchActive = Boolean(String(search || '').trim());
  const rangeStart = filteredRows.length === 0 ? 0 : pageSize <= 0 ? 1 : (page - 1) * pageSize + 1;
  const rangeEnd = pageSize <= 0 ? filteredRows.length : Math.min(page * pageSize, filteredRows.length);

  async function handleCreate(e) {
    e.preventDefault();
    const nombre = String(newNombre || '').trim();
    const descripcion = String(newDescripcion || '').trim();
    if (!nombre) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.create}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre,
          descripcion,
          is_active: newIsActive ? 1 : 0
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const created = json.delivery;
      if (created) {
        setDeliveries((prev) => {
          const next = newIsActive
            ? prev.map((row) => ({ ...row, is_active: 0 }))
            : prev;
          return sortRows([...next, created]);
        });
      } else {
        await loadRows();
      }
      setNewNombre('');
      setNewDescripcion('');
      setNewIsActive(true);
      setCreateOpen(false);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo crear',
        text: err.message || 'Error al crear delivery.'
      });
    } finally {
      setCreating(false);
    }
  }

  function closeCreate() {
    if (creating) return;
    setCreateOpen(false);
    setNewNombre('');
    setNewDescripcion('');
    setNewIsActive(true);
  }

  function openEdit(row) {
    setEditRow(row);
    setEditNombre(String(row?.nombre || ''));
    setEditDescripcion(String(row?.descripcion || ''));
    setEditIsActive(Number(row?.is_active) === 1);
  }

  function closeEdit() {
    if (editSaving) return;
    setEditRow(null);
    setEditNombre('');
    setEditDescripcion('');
    setEditIsActive(false);
  }

  useEffect(() => {
    if (!editRow) return undefined;
    const onKeyDown = (evt) => {
      if (evt.key === 'Escape' && !editSaving) closeEdit();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editRow, editSaving]);

  async function submitEdit(e) {
    e.preventDefault();
    if (!editRow || editSaving) return;
    const id = Number(editRow.id);
    if (!Number.isFinite(id) || id <= 0) return;
    const nombre = String(editNombre || '').trim();
    const descripcion = String(editDescripcion || '').trim();
    if (!nombre) return;

    setEditSaving(true);
    setSavingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.update(id)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre,
          descripcion,
          is_active: editIsActive ? 1 : 0
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json.delivery || { ...editRow, nombre, descripcion, is_active: editIsActive ? 1 : 0 };
      setDeliveries((prev) => {
        const next = prev.map((row) => {
          if (Number(row.id) === id) return { ...row, ...updated };
          if (editIsActive) return { ...row, is_active: 0 };
          return row;
        });
        return sortRows(next);
      });
      closeEdit();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo editar',
        text: err.message || 'Error al actualizar delivery.'
      });
    } finally {
      setEditSaving(false);
      setSavingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  async function requestToggle(row, currentlyActive) {
    const nextActive = !currentlyActive;
    const confirm = await fireConfirmDialog({
      title: nextActive ? 'Activar delivery' : 'Desactivar delivery',
      html: buildCrmSwalBody({
        lead: nextActive
          ? 'Este delivery quedará como el único activo.'
          : 'Quedará inactivo hasta volver a activarlo.',
        details: [{ label: 'Delivery', value: String(row?.nombre || '').trim() }],
        items: nextActive
          ? ['Si había otro activo, se apaga automáticamente']
          : ['No se elimina del catálogo']
      }),
      icon: nextActive ? 'question' : 'warning',
      confirmButtonText: nextActive ? 'Activar' : 'Desactivar',
      cancelButtonText: 'Cancelar',
      width: 'min(420px, 92vw)'
    });
    if (!confirm.isConfirmed) return;
    await toggleActive(row, nextActive);
  }

  async function toggleActive(row, nextActive) {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) return;
    setSavingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.patch(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: nextActive ? 1 : 0 })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      const updated = json.delivery || { ...row, is_active: nextActive ? 1 : 0 };
      setDeliveries((prev) => sortRows(prev.map((item) => {
        if (Number(item.id) === id) return { ...item, ...updated };
        if (nextActive) return { ...item, is_active: 0 };
        return item;
      })));
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo actualizar',
        text: err.message || 'Error al cambiar el estado.'
      });
    } finally {
      setSavingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  async function requestDelete(row) {
    const id = Number(row.id);
    if (!Number.isFinite(id) || id <= 0) return;
    const nombre = String(row?.nombre || '').trim();
    const confirm = await fireConfirmDialog({
      title: 'Eliminar delivery',
      html: buildCrmSwalBody({
        lead: 'Se quitará esta configuración del catálogo.',
        details: [{ label: 'Delivery', value: nombre }],
        items: ['Esta acción no se puede deshacer']
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      width: 'min(420px, 92vw)'
    });
    if (!confirm.isConfirmed) return;

    setDeletingIds((prev) => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}${ENDPOINTS.delete(id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      setDeliveries((prev) => prev.filter((rowItem) => Number(rowItem.id) !== id));
      showToast({
        title: 'Eliminado',
        text: nombre ? `${nombre} fue quitado del catálogo.` : 'Delivery eliminado.'
      });
    } catch (e) {
      fireCrmAlert({
        icon: 'error',
        title: 'No se pudo eliminar',
        html: buildCrmSwalBody({
          lead: String(e?.message || 'Ocurrió un error al eliminar delivery.')
        }),
        confirmButtonText: 'Entendido',
        width: 'min(420px, 92vw)'
      });
    } finally {
      setDeletingIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }

  if (loading) {
    return (
      <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true" style={{ height: '100vh', margin: '15px' }}>
        <div className="crm-gd-spinner" aria-hidden="true" />
        <div className="crm-gd-loading__title">Cargando delivery</div>
        <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Consultando configuración…</p>
      </div>
    );
  }

  return (
    <section className="crm-amv-page crm-gd-page crm-pbody">
      {error ? <div className="crm-amv-alert" role="alert">{error}</div> : null}

      <section className="crm-amv-hero-panel" aria-labelledby="delivery-create-title">
        <div className="crm-amv-hero-panel__head">
          <div className="crm-amv-hero-panel__brand">
            <div className="crm-amv-hero-panel__icon" aria-hidden="true">🏍️</div>
            <div className="crm-amv-hero-panel__titles">
              <p className="crm-amv-hero-panel__eyebrow">Configuración operativa</p>
              <h2 id="delivery-create-title" className="crm-amv-hero-panel__title">
                Delivery
              </h2>
            </div>
          </div>
          <div className="crm-amv-hero-panel__stats" aria-label="Resumen del catálogo">
            <button
              type="button"
              className="crm-amv-hero-panel__submit"
              onClick={() => setCreateOpen(true)}
            >
              Agregar
            </button>
            <div className="crm-amv-hero-stat">
              <span className="crm-amv-hero-stat__num">{deliveries.length}</span>
              <span className="crm-amv-hero-stat__label">Total</span>
            </div>
            <div className="crm-amv-hero-stat crm-amv-hero-stat--visible">
              <span className="crm-amv-hero-stat__num">{activeCount}</span>
              <span className="crm-amv-hero-stat__label">Activo</span>
            </div>
            <div className="crm-amv-hero-stat crm-amv-hero-stat--hidden">
              <span className="crm-amv-hero-stat__num">{inactiveCount}</span>
              <span className="crm-amv-hero-stat__label">Inactivos</span>
            </div>
          </div>
        </div>
      </section>

      {createOpen ? (
        <div className="crm-amv-edit-backdrop" role="presentation" onClick={closeCreate}>
          <div
            className="crm-amv-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delivery-create-modal-title"
            onClick={(evt) => evt.stopPropagation()}
          >
            <div className="crm-amv-edit-modal__head">
              <div className="crm-amv-edit-modal__brand">
                <span className="crm-amv-edit-modal__glyph" aria-hidden="true">
                  <UiIcon name="plus" size={16} />
                </span>
                <div>
                  <p className="crm-amv-edit-modal__eyebrow">Delivery</p>
                  <h3 id="delivery-create-modal-title" className="crm-amv-edit-modal__title">Agregar configuración</h3>
                </div>
              </div>
              <button type="button" className="crm-amv-edit-modal__close" onClick={closeCreate} disabled={creating} aria-label="Cerrar">
                ×
              </button>
            </div>
            <form className="crm-amv-edit-modal__form" onSubmit={handleCreate}>
              <label className="crm-amv-edit-modal__field" htmlFor="delivery-new-nombre">
                <span className="crm-amv-edit-modal__field-label">Nombre</span>
                <input
                  id="delivery-new-nombre"
                  className="crm-amv-edit-modal__input"
                  type="text"
                  placeholder="Ej: Delivery estándar"
                  value={newNombre}
                  onChange={(e) => setNewNombre(e.target.value)}
                  disabled={creating}
                  autoComplete="off"
                  autoFocus
                />
              </label>
              <label className="crm-amv-edit-modal__field" htmlFor="delivery-new-descripcion">
                <span className="crm-amv-edit-modal__field-label">Importe</span>
                <input
                  id="delivery-new-descripcion"
                  className="crm-amv-edit-modal__input"
                  type="number"
                  placeholder="Ej: 2500"
                  value={newDescripcion}
                  onChange={(e) => setNewDescripcion(e.target.value)}
                  disabled={creating}
                  autoComplete="off"
                />
              </label>
              <label className="crm-amv-row__switch-wrap" style={{ justifyContent: 'space-between' }}>
                <span className="crm-amv-row__switch-label">Dejar activo</span>
                <span className="crm-amv-switch-control">
                  <input
                    className="crm-amv-switch-input"
                    type="checkbox"
                    checked={newIsActive}
                    onChange={(e) => setNewIsActive(e.target.checked)}
                    disabled={creating}
                  />
                  <span className="crm-amv-switch-track" />
                </span>
              </label>
              <p className="crm-amv-edit-modal__hint">
                Si lo activás, cualquier otro delivery activo se desactiva automáticamente.
              </p>
              <div className="crm-amv-edit-modal__actions">
                <button type="button" className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--ghost" onClick={closeCreate} disabled={creating}>
                  Cancelar
                </button>
                <button type="submit" className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--primary" disabled={creating || !String(newNombre || '').trim()}>
                  {creating ? 'Guardando…' : 'Agregar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editRow ? (
        <div className="crm-amv-edit-backdrop" role="presentation" onClick={closeEdit}>
          <div
            className="crm-amv-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delivery-edit-title"
            onClick={(evt) => evt.stopPropagation()}
          >
            <div className="crm-amv-edit-modal__head">
              <div className="crm-amv-edit-modal__brand">
                <span className="crm-amv-edit-modal__glyph" aria-hidden="true">
                  <UiIcon name="edit" size={16} />
                </span>
                <div>
                  <p className="crm-amv-edit-modal__eyebrow">Delivery</p>
                  <h3 id="delivery-edit-title" className="crm-amv-edit-modal__title">Editar configuración</h3>
                </div>
              </div>
              <button type="button" className="crm-amv-edit-modal__close" onClick={closeEdit} disabled={editSaving} aria-label="Cerrar">
                ×
              </button>
            </div>
            <form className="crm-amv-edit-modal__form" onSubmit={submitEdit}>
              <label className="crm-amv-edit-modal__field" htmlFor="delivery-edit-nombre">
                <span className="crm-amv-edit-modal__field-label">Nombre</span>
                <input
                  id="delivery-edit-nombre"
                  className="crm-amv-edit-modal__input"
                  type="text"
                  value={editNombre}
                  onChange={(e) => setEditNombre(e.target.value)}
                  disabled={editSaving}
                  autoComplete="off"
                  autoFocus
                />
              </label>
              <label className="crm-amv-edit-modal__field" htmlFor="delivery-edit-descripcion">
                <span className="crm-amv-edit-modal__field-label">Importe</span>
                <input
                  id="delivery-edit-descripcion"
                  className="crm-amv-edit-modal__input"
                  type="number"
                  value={editDescripcion}
                  onChange={(e) => setEditDescripcion(e.target.value)}
                  disabled={editSaving}
                  autoComplete="off"
                />
              </label>
              <label className="crm-amv-row__switch-wrap" style={{ justifyContent: 'space-between' }}>
                <span className="crm-amv-row__switch-label">Activo</span>
                <span className="crm-amv-switch-control">
                  <input
                    className="crm-amv-switch-input"
                    type="checkbox"
                    checked={editIsActive}
                    onChange={(e) => setEditIsActive(e.target.checked)}
                    disabled={editSaving}
                  />
                  <span className="crm-amv-switch-track" />
                </span>
              </label>
              <p className="crm-amv-edit-modal__hint">
                Solo un delivery puede quedar activo al mismo tiempo.
              </p>
              <div className="crm-amv-edit-modal__actions">
                <button type="button" className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--ghost" onClick={closeEdit} disabled={editSaving}>
                  Cancelar
                </button>
                <button type="submit" className="crm-amv-edit-modal__btn crm-amv-edit-modal__btn--primary" disabled={editSaving || !String(editNombre || '').trim()}>
                  {editSaving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className="crm-amv-card crm-gd-card">
        <div className="crm-amv-datatable-toolbar">
          <div className="crm-amv-datatable-toolbar__search">
            <CwListSearch
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por nombre o importe"
            />
          </div>
          <div className="crm-amv-datatable-toolbar__field">
            <label htmlFor="delivery-page-size">Mostrar</label>
            <select
              id="delivery-page-size"
              className="crm-amv-select"
              value={pageSize <= 0 ? 'ALL' : String(pageSize)}
              onChange={(e) => {
                const v = e.target.value;
                setPageSize(v === 'ALL' ? 0 : Number(v));
                setPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
              <option value="ALL">Todos</option>
            </select>
            <span className="crm-amv-datatable-toolbar__suffix">por página</span>
          </div>
        </div>

        {deliveries.length === 0 ? (
          <div className="crm-amv-state crm-amv-state--empty" role="status">
            <span className="crm-amv-state__icon" aria-hidden="true">🏍️</span>
            <p className="crm-amv-state__title">Sin configuraciones de delivery</p>
            <p className="crm-amv-state__hint">Agregá una y definí cuál queda activa.</p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="crm-amv-state crm-amv-state--no-match" role="status">
            <span className="crm-amv-state__icon" aria-hidden="true">🔎</span>
            <p className="crm-amv-state__title">Sin coincidencias</p>
            <p className="crm-amv-state__hint">Probá con otro texto o borrá el filtro.</p>
            <button type="button" className="crm-amv-state__btn" onClick={() => { setSearch(''); setPage(1); }}>
              Limpiar búsqueda
            </button>
          </div>
        ) : (
          <>
            <div className="crm-amv-table-wrap">
              <table className="crm-amv-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Importe</th>
                    <th>Estado</th>
                    <th className="crm-amv-table__th-switch">Activo</th>
                    <th className="crm-amv-table__th-actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((row) => {
                    const isActive = Number(row.is_active) === 1;
                    const rowId = Number(row.id);
                    const isSaving = Boolean(savingIds[rowId]);
                    const isDeleting = Boolean(deletingIds[rowId]);
                    const isBusy = isSaving || isDeleting;
                    return (
                      <tr key={row.id} className={`crm-amv-table__row${isActive ? ' is-visible' : ''}${isBusy ? ' is-saving' : ''}`}>
                        <td>
                          <div className="crm-amv-table__doctor">
                            {isActive ? <span className="crm-amv-table__doctor-dot" aria-hidden="true" /> : null}
                            <span className="crm-amv-table__doctor-name">{row.nombre}</span>
                          </div>
                        </td>
                        <td>{row.descripcion || '—'}</td>
                        <td>
                          {isActive
                            ? <span className="crm-amv-pill crm-amv-pill--visible">Activo</span>
                            : <span className="crm-amv-pill crm-amv-pill--hidden">Inactivo</span>}
                        </td>
                        <td className="crm-amv-table__switch-cell">
                          <label
                            className="crm-amv-row__switch-wrap"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isBusy) return;
                              void requestToggle(row, isActive);
                            }}
                          >
                            <span className="crm-amv-row__switch-label">
                              {isActive ? 'Activo' : 'Inactivo'}
                            </span>
                            <span className="crm-amv-switch-control">
                              <input
                                className="crm-amv-switch-input"
                                type="checkbox"
                                checked={isActive}
                                readOnly
                                tabIndex={-1}
                                aria-hidden="true"
                              />
                              <span
                                className="crm-amv-switch-track"
                                role="switch"
                                aria-checked={isActive}
                                aria-label={`${isActive ? 'Desactivar' : 'Activar'} ${row.nombre}`}
                              />
                            </span>
                          </label>
                        </td>
                        <td className="crm-amv-table__actions-cell">
                          <div className="crm-amv-row-actions">
                            <button
                              type="button"
                              className="crm-amv-row-action-btn"
                              onClick={() => {
                                if (isBusy) return;
                                openEdit(row);
                              }}
                              disabled={isBusy}
                              title={`Editar ${row.nombre}`}
                              aria-label={`Editar ${row.nombre}`}
                            >
                              <UiIcon name="edit" size={14} />
                            </button>
                            <button
                              type="button"
                              className="crm-amv-row-action-btn crm-amv-row-action-btn--danger"
                              onClick={() => {
                                if (isBusy) return;
                                void requestDelete(row);
                              }}
                              disabled={isBusy}
                              title={`Eliminar ${row.nombre}`}
                              aria-label={`Eliminar ${row.nombre}`}
                            >
                              <UiIcon name="trash" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <AmvTablePager
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              filteredCount={filteredRows.length}
              totalCount={deliveries.length}
              searchActive={searchActive}
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPage}
              entityLabel="deliveries"
            />
          </>
        )}
      </div>
    </section>
  );
}

export default DeliveryConfig;
